import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { setUser } from '../../../features/authSlice'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import {
  useAcknowledgeReceiptVaultRulesMutation,
  useGetReceiptVaultUploadRulesQuery,
} from '../../../services/authApi'
import { ReceiptVaultRuleItem, ReceiptVaultRulesPayload } from './receiptVaultRuleUtils'

type Props = {
  show: boolean
  onAcknowledged: () => void
  onCancel: () => void
}

const ReceiptVaultRulesAcknowledgeModal = ({ show, onAcknowledged, onCancel }: Props) => {
  const dispatch = useDispatch()
  const auth = useAppSelector(selectAuth)
  const [accepted, setAccepted] = useState(false)
  const { data, isFetching, isError } = useGetReceiptVaultUploadRulesQuery(undefined, {
    skip: !show,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })
  const [acknowledge, { isLoading }] = useAcknowledgeReceiptVaultRulesMutation()

  useEffect(() => {
    if (show) setAccepted(false)
  }, [show])

  const rulesObj: ReceiptVaultRulesPayload = useMemo(() => {
    const raw = data?.result?.rules
    if (raw && typeof raw === 'object') return raw as ReceiptVaultRulesPayload
    try {
      if (data?.result?.rulesJson) return JSON.parse(data.result.rulesJson) as ReceiptVaultRulesPayload
    } catch {
      /* ignore */
    }
    return {
      title: 'Receipt Vault Upload Rules',
      intro: 'Please review these rules before uploading receipts to the Receipt Vault.',
      rules: [
        'Upload clear, complete receipts that show merchant name, date, and total amount.',
        'Do not upload unrelated personal documents or blank/unreadable images.',
        'Use Corporate Credit Card = Yes only when the receipt is for a corporate card transaction.',
        'You are responsible for ensuring uploaded receipts are accurate and business-related.',
      ],
    }
  }, [data])

  const handleAcknowledge = async () => {
    if (!accepted) {
      toast.error('Please acknowledge the Receipt Vault upload rules to continue.')
      return
    }
    try {
      await acknowledge(undefined).unwrap()
      dispatch(
        setUser({
          ...auth,
          isReceiptVaultRulesAcknowledgementAccepted: true,
        } as any)
      )
      onAcknowledged()
    } catch {
      toast.error('Unable to save acknowledgement. Please try again.')
    }
  }

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      backdrop="static"
      dialogClassName="modal-dialog modal-lg ss-receipt-vault-rules-dialog"
      contentClassName="ss-receipt-vault-rules-modal"
    >
      <div className="modal-header">
        <h2 className="mb-0">{rulesObj.title || 'Receipt Vault Upload Rules'}</h2>
        <button type="button" className="btn-close" onClick={onCancel} aria-label="Close" />
      </div>
      <div className="modal-body">
        {isFetching ? (
          <div className="text-center py-5">
            <span className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            {isError ? (
              <div className="alert alert-warning">
                Could not load rules from settings. Showing default guidance.
              </div>
            ) : null}
            {rulesObj.intro ? <p className="fs-6 fw-semibold ss-rv-rules-intro mb-3">{rulesObj.intro}</p> : null}
            <ol className="mb-4 ps-3 ss-rv-rules-list">
              {(rulesObj.rules || []).map((rule, idx) => (
                <li key={idx} className="mb-2">
                  <ReceiptVaultRuleItem rule={rule} />
                </li>
              ))}
            </ol>
            <div className={`ss-rv-ack-box form-check${accepted ? ' is-checked' : ''}`}>
              <input
                className="form-check-input ss-form-check-input"
                type="checkbox"
                id="ackReceiptVaultRules"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <label className="form-check-label ss-rv-ack-label" htmlFor="ackReceiptVaultRules">
                I acknowledge that I have read and agree to the Receipt Vault upload rules.
              </label>
            </div>
          </>
        )}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-light" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleAcknowledge}
          disabled={isLoading || isFetching || !accepted}
        >
          {isLoading ? 'Saving…' : 'Continue to Upload'}
        </button>
      </div>
    </Modal>
  )
}

export default ReceiptVaultRulesAcknowledgeModal
